import { useCallback, useEffect, useRef, useState } from "react";

import { useStableValue } from "@/ds";
import { listObras } from "@/modules/obras/obras.gateway";
import { DEFAULT_OBRAS_SORT } from "@/modules/obras/obras-lista.types";
import type { ObrasFilters, ObrasQueryState, ObrasSort } from "@/modules/obras/obras-lista.types";
import { describeListingError, LISTING_SEARCH_DEBOUNCE_MS } from "@/shared/hooks";

export const OBRAS_LISTA_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar as obras.";
export const OBRAS_LISTA_ERROR_MESSAGE = "Não foi possível carregar as obras.";

const INITIAL_STATE: ObrasQueryState = {
  status: "loading",
  items: [],
  total: 0,
  errorMessage: null,
};

const MESSAGES = { forbidden: OBRAS_LISTA_FORBIDDEN_MESSAGE, error: OBRAS_LISTA_ERROR_MESSAGE };

export interface UseObrasListaParams {
  search: string;
  filters: ObrasFilters;
  page: number;
  pageSize: number;
  sort?: ObrasSort;
}

/** Visão Lista: paginação, ordenação e filtros no servidor. */
export function useObrasLista({
  search,
  filters,
  page,
  pageSize,
  sort = DEFAULT_OBRAS_SORT,
}: UseObrasListaParams) {
  const [state, setState] = useState<ObrasQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  const appliedFilters = useStableValue(filters);
  const { field: sortField, direction: sortDirection } = sort;

  useEffect(() => {
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listObras({
          busca: term,
          filters: appliedFilters,
          page,
          pageSize,
          sort: { field: sortField, direction: sortDirection },
          signal: controller.signal,
        })
          .then(({ items, total }) => {
            if (!cancelled) setState({ status: "success", items, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (!cancelled)
              setState({ items: [], total: 0, ...describeListingError(error, MESSAGES) });
          });
      },
      shouldDebounce ? LISTING_SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, appliedFilters, page, pageSize, sortField, sortDirection, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
